import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Truck,
  Package,
  FileText,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface DeliveryItemForm {
  soItemId: string;
  productId: string | null;
  productName?: string;
  productCode?: string | null;
  warehouseId?: string | null;
  pendingQty: number;
  quantity: number;
}

interface DeliveryForm {
  salesOrderId: string;
  warehouseId?: string;
  shippingMethod?: string;
  trackingNumber?: string;
  carrierName?: string;
  notes?: string;
  items: DeliveryItemForm[];
}

export function NewDeliveryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const [searchParams] = useSearchParams();
  const preselectedOrderId = searchParams.get('salesOrderId') || '';

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm<DeliveryForm>({
    defaultValues: {
      salesOrderId: preselectedOrderId,
      items: [],
    },
  });

  const { fields: items, replace: replaceItems } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedValues = watch();
  const selectedOrderId = watchedValues.salesOrderId;

  // Queries - fetch both CONFIRMED and PARTIALLY_SHIPPED orders (they may have pending items)
  const { data: confirmedOrders } = trpc.tradingSalesOrder.list.useQuery({
    status: 'CONFIRMED' as any,
  });

  const { data: partiallyShippedOrders } = trpc.tradingSalesOrder.list.useQuery({
    status: 'PARTIALLY_SHIPPED' as any,
  });

  const { data: pendingItems, isLoading: loadingPendingItems } = trpc.tradingSalesOrder.getPendingItems.useQuery(
    selectedOrderId,
    { enabled: !!selectedOrderId }
  );

  const { data: orderDetail } = trpc.tradingSalesOrder.byId.useQuery(
    selectedOrderId,
    { enabled: !!selectedOrderId }
  );

  // Combine confirmed and partially shipped orders
  const orders = [
    ...(confirmedOrders?.orders ?? []),
    ...(partiallyShippedOrders?.orders ?? []),
  ];

  // Create mutation
  const createMutation = trpc.tradingDelivery.create.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('sales.deliveries.created'));
      navigate(`/trading/sales/deliveries/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  // Handle order selection
  const handleOrderSelect = (orderId: string) => {
    setValue('salesOrderId', orderId);
    // Items will be loaded via useQuery
  };

  // When pending items change, update the form
  useState(() => {
    if (pendingItems && pendingItems.length > 0) {
      replaceItems(
        pendingItems.map((item) => ({
          soItemId: item.id,
          productId: item.productId,
          productName: item.productName,
          productCode: item.productCode,
          warehouseId: item.warehouseId,
          pendingQty: item.pendingQty,
          quantity: item.pendingQty, // Default to full pending quantity
        }))
      );
    }
  });

  // Update items when pending items load
  if (pendingItems && items.length === 0 && pendingItems.length > 0) {
    replaceItems(
      pendingItems.map((item) => ({
        soItemId: item.id,
        productId: item.productId,
        productName: item.productName,
        productCode: item.productCode,
        warehouseId: item.warehouseId,
        pendingQty: item.pendingQty,
        quantity: item.pendingQty,
      }))
    );
  }

  const onSubmit = (data: DeliveryForm) => {
    if (!data.salesOrderId) {
      showErrorToast(t('sales.validation.selectSalesOrder'));
      return;
    }

    // Filter items with quantity > 0
    const itemsToShip = data.items.filter((item) => item.quantity > 0);

    if (itemsToShip.length === 0) {
      showErrorToast(t('sales.validation.enterQuantity'));
      return;
    }

    // Validate quantities
    for (const item of itemsToShip) {
      if (item.quantity > item.pendingQty) {
        showErrorToast(`Quantity cannot exceed pending quantity for ${item.productName}`);
        return;
      }
    }

    createMutation.mutate({
      salesOrderId: data.salesOrderId,
      warehouseId: data.warehouseId || undefined,
      shippingMethod: data.shippingMethod || undefined,
      trackingNumber: data.trackingNumber || undefined,
      carrierName: data.carrierName || undefined,
      notes: data.notes || undefined,
      items: itemsToShip.map((item) => ({
        soItemId: item.soItemId,
        productId: item.productId ?? undefined,
        quantity: item.quantity,
      })),
    });
  };

  const totalItemsToShip = watchedValues.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/trading/sales/deliveries"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          <h1 className="text-2xl font-bold">{t('sales.deliveries.addDelivery')}</h1>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={createMutation.isPending || !selectedOrderId || totalItemsToShip === 0}
        >
          {createMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t('sales.deliveries.createDelivery')}
        </Button>
      </div>

      <form className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Sales Order Selection */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('sales.deliveries.selectOrder')}
            </h2>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('sales.deliveries.salesOrder')} *</Label>
                <Combobox
                  options={orders.map((o) => ({
                    value: o.id,
                    label: `${o.orderNumber} - ${o.customerName} (${o.itemCount} items)`,
                  }))}
                  value={selectedOrderId || ''}
                  onValueChange={handleOrderSelect}
                  placeholder={t('sales.deliveries.selectOrder')}
                  searchPlaceholder={t('sales.orders.searchPlaceholder')}
                />
              </div>

              {orderDetail && (
                <div className="bg-muted/50 p-4">
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('sales.orders.customer')}</span>
                      <span className="font-medium">{orderDetail.contactName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('sales.orders.company')}</span>
                      <span>{orderDetail.companyName || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('sales.orders.shippingAddress')}</span>
                      <span>{orderDetail.shippingAddress || '-'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Shipping Details */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Truck className="h-5 w-5" />
              {t('sales.deliveries.shippingDetails')}
            </h2>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="shippingMethod">{t('sales.deliveries.shippingMethod')}</Label>
                <Input
                  id="shippingMethod"
                  {...register('shippingMethod')}
                  placeholder={t('sales.deliveries.shippingMethodPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="carrierName">{t('sales.deliveries.carrier')}</Label>
                <Input
                  id="carrierName"
                  {...register('carrierName')}
                  placeholder={t('sales.deliveries.carrierPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="trackingNumber">{t('sales.deliveries.trackingNumber')}</Label>
                <Input
                  id="trackingNumber"
                  {...register('trackingNumber')}
                  placeholder={t('sales.deliveries.trackingPlaceholder')}
                />
              </div>
            </div>
          </div>

          {/* Items to Ship */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 font-semibold">
                <Package className="h-5 w-5" />
                {t('sales.deliveries.itemsToShip')}
              </h2>
            </div>

            {!selectedOrderId && (
              <div className="flex flex-col items-center justify-center py-8 text-center border border-border bg-muted/30">
                <FileText className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t('sales.deliveries.selectOrderFirst')}
                </p>
              </div>
            )}

            {selectedOrderId && loadingPendingItems && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            {selectedOrderId && !loadingPendingItems && pendingItems?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-center border border-green-200 bg-green-50">
                <CheckCircle className="h-10 w-10 text-green-500 mb-2" />
                <p className="text-sm text-green-700 font-medium">
                  {t('sales.deliveries.allItemsShipped')}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {t('sales.deliveries.noItemsToShip')}
                </p>
              </div>
            )}

            {selectedOrderId && !loadingPendingItems && items.length > 0 && (
              <div className="space-y-3">
                {items.map((field, index) => {
                  const item = watchedValues.items[index];

                  return (
                    <div key={field.id} className="border border-border p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium">{field.productName}</p>
                          {field.productCode && (
                            <p className="text-xs text-muted-foreground font-mono">{field.productCode}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            {t('sales.deliveries.pending')}: <span className="font-medium text-foreground">{field.pendingQty}</span>
                          </p>
                        </div>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('sales.deliveries.quantityToShip')}</Label>
                          <Input
                            type="number"
                            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                            min="0"
                            max={field.pendingQty}
                          />
                          {(item?.quantity || 0) > field.pendingQty && (
                            <p className="text-xs text-red-500 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Cannot exceed pending quantity
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <FileText className="h-5 w-5" />
              {t('sales.deliveries.notes')}
            </h2>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('sales.deliveries.deliveryNotes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('sales.deliveries.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Sidebar - Delivery Summary */}
        <div className="space-y-6">
          <div className="border border-border bg-card p-6 sticky top-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Truck className="h-5 w-5" />
              {t('sales.deliveries.summary')}
            </h2>

            <div className="space-y-3 text-sm">
              {orderDetail && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('sales.orders.orderNumber')}</span>
                    <span className="font-medium font-mono">{orderDetail.orderNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('sales.orders.customer')}</span>
                    <span className="font-medium">{orderDetail.contactName}</span>
                  </div>
                </>
              )}

              <div className="border-t my-3" />

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.deliveries.itemsToShip')}</span>
                <span className="font-medium">{items.filter((i) => (watchedValues.items?.find((wi) => wi.soItemId === i.soItemId)?.quantity || 0) > 0).length}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.deliveries.totalUnits')}</span>
                <span className="font-medium">{totalItemsToShip}</span>
              </div>

              {watchedValues.shippingMethod && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.deliveries.shippingMethod')}</span>
                  <span>{watchedValues.shippingMethod}</span>
                </div>
              )}

              {watchedValues.carrierName && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.deliveries.carrier')}</span>
                  <span>{watchedValues.carrierName}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-2">
              <Button
                type="button"
                className="w-full"
                onClick={handleSubmit(onSubmit)}
                disabled={createMutation.isPending || !selectedOrderId || totalItemsToShip === 0}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t('sales.deliveries.createDelivery')}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {t('sales.deliveries.stockNote')}
              </p>
            </div>

            {/* Validation Warnings */}
            {(!selectedOrderId || totalItemsToShip === 0) && (
              <div className="mt-4 space-y-2">
                {!selectedOrderId && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.selectSalesOrderToContinue')}</p>
                )}
                {selectedOrderId && totalItemsToShip === 0 && (
                  <p className="text-xs text-yellow-600">{t('sales.validation.enterQuantityForItem')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
